import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { t } from "../i18n";
import useLrc from "../hooks/useLrc";

function formatTime(seconds) {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ——— Icons ———
function IconPrev() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
    </svg>
  );
}
function IconPlay() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}
function IconNext() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 6h2v12h-2zm-3.5 6l-8.5 6V6z" />
    </svg>
  );
}
function IconRepeatAll() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}
function IconRepeatOne() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      <text x="12" y="15" textAnchor="middle" fontSize="9" fill="currentColor" stroke="none">1</text>
    </svg>
  );
}
function IconShuffle() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  );
}
function IconChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function IconMore() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

/** Menu item for the "more" drawer */
function MoreMenuItem({ icon, label, handler, badge, highlight }) {
  return (
    <button
      onClick={() => { handler?.(); }}
      className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left transition ${
        highlight
          ? "text-amber-600 bg-amber-50"
          : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <span className="w-5 h-5 flex items-center justify-center shrink-0">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      {badge != null && (
        <span className="w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">
          {badge}
        </span>
      )}
    </button>
  );
}

const SOURCES = [
  { key: "playlist", icon: "🎵" },
  { key: "liked", icon: "❤️" },
  { key: "favorites", icon: "⭐" },
  { key: "temp", icon: "📋" },
  { key: "explore", icon: "🔍" },
];

const LOOP_ORDER = ["repeat-all", "repeat-one", "shuffle"];

function LoopIcon({ mode }) {
  if (mode === "repeat-all") return <IconRepeatAll />;
  if (mode === "repeat-one") return <IconRepeatOne />;
  if (mode === "shuffle") return <IconShuffle />;
  return <IconRepeatAll />;
}

export default function PlayerBar({
  currentTrack,
  currentScene,
  isPlaying,
  audioRef,
  likedTracks,
  favoritedTracks,
  playlistSource,
  playlistName,
  sourceTrackCount = 0,
  loopMode,
  onTogglePlay,
  onPrev,
  onNext,
  onToggleLike,
  onToggleFavorite,
  onDislike,
  onShare,
  onOpenPlayer,
  onOpenImmersive,
  onTogglePlaylist,
  onChangeSource,
  onChangeLoopMode,
  lang = 'zh',
}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [showVolume, setShowVolume] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const { currentLine: currentLyric, loading: _lrcLoading } = useLrc(currentTrack, currentTime);
  const progressRef = useRef(null);
  const sourceRef = useRef(null);
  const moreRef = useRef(null);

  // Sync audio time updates
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(audio.duration || 0);
    const onLoadedMeta = () => setDuration(audio.duration || 0);
    const onWaiting = () => setIsWaiting(true);
    const onCanPlay = () => setIsWaiting(false);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("loadedmetadata", onLoadedMeta);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("playing", onCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("loadedmetadata", onLoadedMeta);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("playing", onCanPlay);
    };
  }, [audioRef, currentTrack]);

  // Reset time when track changes
  useEffect(() => {
    setCurrentTime(0);
  }, [currentTrack?.id]);

  // Close source menu on outside click
  useEffect(() => {
    if (!showSourceMenu) return;
    const handleClick = (e) => {
      if (sourceRef.current && !sourceRef.current.contains(e.target)) {
        setShowSourceMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showSourceMenu]);

  // Close more menu on outside click
  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMoreMenu]);

  const handleSeek = useCallback(
    (e) => {
      const rect = progressRef.current?.getBoundingClientRect();
      if (!rect || !audioRef?.current) return;
      const pct = (e.clientX - rect.left) / rect.width;
      const time = pct * duration;
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    },
    [duration, audioRef]
  );

  const handleVolumeChange = useCallback(
    (e) => {
      const v = parseFloat(e.target.value);
      setVolume(v);
      if (audioRef?.current) audioRef.current.volume = v;
    },
    [audioRef]
  );

  function cycleLoopMode() {
    if (!onChangeLoopMode) return;
    const idx = LOOP_ORDER.indexOf(loopMode);
    const next = LOOP_ORDER[(idx + 1) % LOOP_ORDER.length];
    onChangeLoopMode(next);
  }

  // Empty state — show minimal bar with source selector + actions
  const isEmpty = !currentTrack;

  const progress = isEmpty ? 0 : duration > 0 ? (currentTime / duration) * 100 : 0;
  const isLiked = !isEmpty && likedTracks?.has(currentTrack.id);
  const isFavorited = !isEmpty && favoritedTracks?.has(currentTrack.id);

  const sceneImage = currentScene?.image;
  const trackName = currentTrack?.name || "";
  const _sceneName = currentScene?.name || "";  // reserved for future use

  const currentSource = SOURCES.find((s) => s.key === playlistSource) || SOURCES[0];

  // Decide if loop mode is applicable
  const showLoopControl = playlistSource !== "explore";

  return (
    <>
    <style>{`
      @keyframes rhythm {
        0%, 100% { transform: scaleY(0.4); }
        50% { transform: scaleY(1); }
      }
      .rhythm-bar {
        animation: rhythm 0.6s ease-in-out infinite;
        transform-origin: bottom;
      }
    `}</style>
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
      {/* Progress bar (clickable) */}
      <div
        ref={progressRef}
        className={`h-1 bg-gray-200 group relative ${isEmpty ? '' : 'cursor-pointer'}`}
        onClick={isEmpty ? undefined : handleSeek}
      >
        <div
          className="h-full bg-indigo-500 transition-all duration-100 relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-indigo-500 opacity-0 group-hover:opacity-100 transition shadow-sm" />
        </div>
      </div>

      {/* Main content */}
      <div className="flex items-center gap-2 px-3 py-2 max-w-7xl mx-auto">
        {isEmpty ? (
          /* Empty state */
          <>
            <div className="flex-1 min-w-0" />
            <div className="text-xs text-gray-400 shrink-0 mr-2">{t(lang, 'exploreSource')}</div>
          </>
        ) : (
          /* Track info */
          <div
            className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer"
            onClick={onOpenPlayer}
          >
            {sceneImage && (
            <div className="relative w-9 h-9 shrink-0">
              <div
                className="w-full h-full rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${sceneImage})` }}
              />
              {/* State indicator overlay */}
              {isWaiting ? (
                <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
                  <div className="flex gap-[2px] items-end h-3">
                    <div className="w-[2px] bg-white/80 rounded-full animate-pulse" style={{height:'100%'}}></div>
                    <div className="w-[2px] bg-white/80 rounded-full animate-pulse" style={{height:'60%',animationDelay:'0.2s'}}></div>
                    <div className="w-[2px] bg-white/80 rounded-full animate-pulse" style={{height:'80%',animationDelay:'0.4s'}}></div>
                  </div>
                </div>
              ) : !isPlaying ? (
                <div className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center">
                  <div className="flex gap-[3px] items-center">
                    <div className="w-[3px] h-[12px] bg-white/80 rounded-sm"></div>
                    <div className="w-[3px] h-[12px] bg-white/80 rounded-sm"></div>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 rounded-lg bg-black/30 flex items-center justify-center">
                  <div className="flex gap-[2px] items-end h-3">
                    <div className="w-[2px] bg-white rounded-full rhythm-bar" style={{height:'40%',animationDelay:'0s'}}></div>
                    <div className="w-[2px] bg-white rounded-full rhythm-bar" style={{height:'100%',animationDelay:'0.15s'}}></div>
                    <div className="w-[2px] bg-white rounded-full rhythm-bar" style={{height:'60%',animationDelay:'0.3s'}}></div>
                    <div className="w-[2px] bg-white rounded-full rhythm-bar" style={{height:'80%',animationDelay:'0.45s'}}></div>
                    <div className="w-[2px] bg-white rounded-full rhythm-bar" style={{height:'50%',animationDelay:'0.6s'}}></div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="min-w-0">
            <Link
              to={currentTrack ? `/track/${currentTrack.id}` : '#'}
              className="text-sm font-medium text-gray-900 truncate leading-tight hover:text-indigo-600 transition inline-block max-w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {trackName}
            </Link>
            {currentLyric && (
              <div className="text-xs text-indigo-500 truncate leading-tight mt-0.5 font-medium">
                ♪ {currentLyric.text}
              </div>
            )}
          </div>
        </div>
        )}

        {/* ─── Source selector ─── */}
        <div className="relative shrink-0" ref={sourceRef}>
          <button
            onClick={() => setShowSourceMenu(!showSourceMenu)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition shrink-0 ${
              showSourceMenu
                ? "bg-indigo-100 text-indigo-700"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            }`}
          >
            <span>{currentSource.icon}</span>
            <span className="hidden sm:inline">{currentSource.key === "playlist" && playlistName ? playlistName : t(lang, currentSource.key === "playlist" ? "playlistSource" : currentSource.key === "liked" ? "likedSource" : currentSource.key === "favorites" ? "favoritesSource" : currentSource.key === "temp" ? "tempSource" : "exploreSource")}</span>
            <IconChevronDown />
          </button>

          {showSourceMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowSourceMenu(false)} />
              <div className="absolute bottom-full right-0 mb-2 w-44 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-20">
                {SOURCES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => {
                      onChangeSource(s.key);
                      setShowSourceMenu(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition ${
                      s.key === playlistSource
                        ? "bg-indigo-50 text-indigo-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>{s.icon}</span>
                    <span>{s.key === "playlist" && playlistName ? playlistName : t(lang, s.key === "playlist" ? "playlistSource" : s.key === "liked" ? "likedSource" : s.key === "favorites" ? "favoritesSource" : s.key === "temp" ? "tempSource" : "exploreSource")}</span>
                    {s.key === playlistSource && (
                      <svg className="ml-auto w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {!isEmpty && (
          <>
        {/* Playback controls */}
        <div className="flex items-center gap-1">
          {/* Previous */}
          <button
            onClick={onPrev}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            title={t(lang, 'previous')}
          >
            <IconPrev />
          </button>

          {/* Play/Pause */}
          <button
            onClick={onTogglePlay}
            className="w-9 h-9 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center transition shadow-sm"
          >
            {isPlaying ? <IconPause /> : <IconPlay />}
          </button>

          {/* Next */}
          <button
            onClick={onNext}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            title={t(lang, 'next')}
          >
            <IconNext />
          </button>
        </div>

        {/* Time */}
        <div className="hidden sm:flex items-center gap-1 min-w-[75px] text-xs text-gray-400 font-mono tabular-nums">
          <span>{formatTime(currentTime)}</span>
          <span className="text-gray-300">/</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Inline action buttons — progressive visibility */}
        <div className="flex items-center gap-0.5">
          {/* Loop mode toggle — hidden lg+ */}
          {showLoopControl && (
            <button
              onClick={cycleLoopMode}
              className={`hidden lg:flex w-8 h-8 rounded-full items-center justify-center transition shrink-0 ${
                loopMode === "shuffle"
                  ? "text-indigo-600 bg-indigo-50"
                  : loopMode === "repeat-one"
                  ? "text-amber-500 bg-amber-50"
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              }`}
              title={
                loopMode === "repeat-all"
                  ? t(lang, 'loopAll')
                  : loopMode === "repeat-one"
                  ? t(lang, 'loopOne')
                  : t(lang, 'shuffleMode')
              }
            >
              <LoopIcon mode={loopMode} />
            </button>
          )}

          {/* Dislike — only xl+ */}
          <button
            onClick={onDislike}
            className={`hidden xl:inline-flex w-8 h-8 rounded-full items-center justify-center transition shrink-0 text-gray-400 hover:text-blue-500 hover:bg-gray-100`}
            title={t(lang, 'dislike')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
              <path d="M15 2h3.5a2 2 0 0 1 2 2l.5 8a2 2 0 0 1-2 2h-4" />
            </svg>
          </button>

          {/* Like — always visible */}
          <button
            onClick={() => onToggleLike(currentTrack.id, currentTrack, currentScene)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition shrink-0 ${
              isLiked
                ? "text-red-500 bg-red-50 hover:bg-red-100"
                : "text-gray-400 hover:text-red-400 hover:bg-gray-100"
            }`}
            title={isLiked ? t(lang, 'unlike') : t(lang, 'like')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>

          {/* Favorite — hidden lg+ */}
          <button
            onClick={() => onToggleFavorite(currentTrack.id, currentTrack, currentScene)}
            className={`hidden lg:inline-flex w-8 h-8 rounded-full items-center justify-center transition shrink-0 ${
              isFavorited
                ? "text-amber-500 bg-amber-50 hover:bg-amber-100"
                : "text-gray-400 hover:text-amber-400 hover:bg-gray-100"
            }`}
            title={isFavorited ? t(lang, 'unfavorite') : t(lang, 'favorite')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>

          {/* Share — only xl+ */}
          <button
            onClick={() => onShare(currentTrack)}
            className="hidden xl:inline-flex w-8 h-8 rounded-full items-center justify-center text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition shrink-0"
            title={t(lang, 'share')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>
        </div>

        {/* ─── "More" drawer button (hidden on xl) ─── */}
        <div className="relative xl:hidden shrink-0" ref={moreRef}>
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition shrink-0 ${
              showMoreMenu
                ? "bg-indigo-100 text-indigo-600"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            }`}
            title="更多操作"
          >
            <IconMore />
          </button>

          {showMoreMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} />
              <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-20">
                {/* Loop */}
                {showLoopControl && (
                  <MoreMenuItem
                    icon={<LoopIcon mode={loopMode} />}
                    label={
                      loopMode === "repeat-all"
                        ? t(lang, 'loopAll')
                        : loopMode === "repeat-one"
                        ? t(lang, 'loopOne')
                        : t(lang, 'shuffleMode')
                    }
                    highlight={loopMode !== "repeat-all"}
                    handler={cycleLoopMode}
                  />
                )}
                {/* Dislike */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                      <path d="M15 2h3.5a2 2 0 0 1 2 2l.5 8a2 2 0 0 1-2 2h-4" />
                    </svg>
                  }
                  label={t(lang, 'dislike')}
                  handler={onDislike}
                />
                {/* Favorite */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  }
                  label={isFavorited ? t(lang, 'unfavorite') : t(lang, 'favorite')}
                  highlight={isFavorited}
                  handler={() => onToggleFavorite(currentTrack.id, currentTrack, currentScene)}
                />
                {/* Share */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  }
                  label={t(lang, 'share')}
                  handler={() => onShare(currentTrack)}
                />
                {/* Volume */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  }
                  label={t(lang, 'volumeLabel')}
                  handler={() => setShowVolume(true)}
                />
                <div className="border-t border-gray-100 my-1" />
                {/* Playlist */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                  }
                  label={t(lang, 'playlist')}
                  badge={sourceTrackCount > 0 ? (sourceTrackCount > 9 ? "9+" : sourceTrackCount) : null}
                  handler={onTogglePlaylist}
                />
                {/* Expand */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  }
                  label={t(lang, 'openPlayer')}
                  handler={onOpenPlayer}
                />
                {/* Immersive */}
                <MoreMenuItem
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                  }
                  label={t(lang, 'openImmersive')}
                  handler={onOpenImmersive}
                />
              </div>
            </>
          )}
        </div>

        {/* Volume — visible md+ on desktop, lg+ on responsive */}
        <div className="relative hidden lg:flex">
          <button
            onClick={() => setShowVolume(!showVolume)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            title={t(lang, 'volumeLabel')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          </button>
          {showVolume && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-gray-900 rounded-lg shadow-lg">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-gray-600 rounded-full appearance-none cursor-pointer accent-indigo-400 rotate-0"
                style={{ writingMode: "horizontal-tb" }}
              />
            </div>
          )}
        </div>

        {/* Playlist — hidden sm+ */}
        <button
          onClick={onTogglePlaylist}
          className={`hidden sm:inline-flex w-8 h-8 rounded-full items-center justify-center transition shrink-0 relative ${
            sourceTrackCount > 0
              ? "text-indigo-600 hover:bg-indigo-50"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          }`}
          title={t(lang, 'playlist')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" />
            <circle cx="18" cy="16" r="3" />
          </svg>
          {sourceTrackCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {sourceTrackCount > 9 ? "9+" : sourceTrackCount}
            </span>
          )}
        </button>

        {/* Expand — hidden sm+ */}
          <button
            onClick={onOpenPlayer}
            className="hidden sm:inline-flex w-8 h-8 rounded-full items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition shrink-0"
            title={t(lang, 'openPlayer')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        {/* Immersive mode — hidden md+ */}
          <button
            onClick={onOpenImmersive}
            className="hidden md:inline-flex w-8 h-8 rounded-full items-center justify-center text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition shrink-0"
            title={t(lang, 'openImmersive')}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </button>
        </>
        )}
      </div>
    </div>
    </>
  );
}
